import React from 'react'
import ImageTextCarouselItem from './image-text-item'
import {ImageTextCarouselProps} from './types'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import {useMediaQueryWithBuilder} from 'hec/builder/hooks/useMediaQueryWithBuilder'
import {Button} from '@/components/cta'
import {Carousel, CarouselContent, CarouselItem} from '@/components/ui/carousel'
import {cn} from '@/lib/utils'
/**
 * ImageTextCarousel Component
 *
 * contentType determines styling and rendering behavior: 'logo' (default) vs 'icons'
 *
 * Desktop layout differences:
 * - 'logo': Items get wider containers (max-width: 200px) for larger logos
 * - 'icons': Items use smaller containers (max-width: 140px)
 *
 * Mobile layout differences:
 * - 'icons': Uses a swipeable Carousel component with 4 slides visible
 * - 'logo': Uses a static flex layout without carousel functionality they will wrap as needed
 * - figma for context https://www.figma.com/design/XpnB3oZoBMl3hW98woA8yW/Home-Page?node-id=326-1548&m=dev
 */

const ImageTextCarousel: React.FC<ImageTextCarouselProps> = (props) => {
    const {isMobile: isBuilderMobile} = useMediaQueryWithBuilder()
    const isMobile = useMediaQuery('sm')
    const {
        ctaPosition,
        items = [],
        ctaStyle,
        buttonColor,
        header,
        ctaText,
        external,
        buttonTextColor,
        contentType
    } = props
    const buttonPosition = ctaPosition || 'bottom'
    const buttonClassName = cn(
        'cursor-pointer',
        ctaStyle === 'link'
            ? 'bg-transparent text-inherit border-none p-0 underline outline-inherit'
            : 'border-none px-5 py-2.5 rounded-md'
    )
    const buttonStyle =
        ctaStyle === 'link'
            ? undefined
            : {
                  backgroundColor: buttonColor || '#0070f3',
                  color: buttonTextColor || 'white'
              }
    return (
        <div className="image-text-carousel flex justify-center items-center ">
            {/* Desktop: row of columns */}

            {!isMobile && !isBuilderMobile ? (
                <div className="desktop carousel  flex-col max-w-[728px] justify-center items-center lg:gap-[20px] lg:flex  lg:w-full lg:max-w-full">
                    <div className="headline-container text-center min-h-[80px] flex justify-center items-center">
                        {header && (
                            <h2 className="headline text-size-225 lg:text-size-heading-lg text-center">
                                {header}
                            </h2>
                        )}
                    </div>
                    {buttonPosition === 'top' && (
                        <div className="cta-container text-center mb-md">
                            {ctaText && (
                                <Button
                                    clickFunction="Link to Page"
                                    style={buttonStyle}
                                    className={cn('text-size-body-lg', buttonClassName)}
                                    url={''}
                                    target={external ? '_blank' : '_self'}
                                    rel={external ? 'noreferrer' : undefined}
                                >
                                    {ctaText}
                                </Button>
                            )}
                        </div>
                    )}

                    <div className="  w-full min-h-[123px] flex flex-wrap justify-center items-center gap-6 lg:gap-8 xl:gap-md lg:flex-row  ">
                        {items.map((item, idx) => (
                            <div
                                key={idx}
                                className="px-2 lg:px-3 max-w-[100px] lg:max-w-[120px] xl:max-w-[140px]"
                            >
                                <ImageTextCarouselItem {...item} contentType={contentType} />
                            </div>
                        ))}
                    </div>
                    {buttonPosition === 'bottom' && (
                        <div className="cta-container text-center mb-md">
                            {ctaText && (
                                <Button
                                    clickFunction="link"
                                    style={buttonStyle}
                                    className={buttonClassName}
                                    url={''}
                                    target={external ? '_blank' : '_self'}
                                    rel={external ? 'noreferrer' : undefined}
                                >
                                    {ctaText}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="mobile-carousel w-full flex-col  justify-center items-center ">
                    <div className="headline-container text-center min-h-[65px] flex justify-center items-center">
                        {header && <h2 className="headline text-size-225 text-center">{header}</h2>}
                    </div>
                    {buttonPosition === 'top' && (
                        <div className="cta-container text-center mb-md">
                            {ctaText && (
                                <Button
                                    clickFunction="Link to Page"
                                    style={buttonStyle}
                                    className={cn('text-size-body-lg', buttonClassName)}
                                    url={''}
                                    target={external ? '_blank' : '_self'}
                                    rel={external ? 'noreferrer' : undefined}
                                >
                                    {ctaText}
                                </Button>
                            )}
                        </div>
                    )}
                    {contentType === 'logo' ? (
                        <div className="grid grid-cols-3 gap-4 px-4">
                            {items.map((item, idx) => (
                                <div key={idx} className="flex justify-center">
                                    <ImageTextCarouselItem {...item} contentType={contentType} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Carousel>
                            <CarouselContent>
                                {items.map((item, idx) => (
                                    <CarouselItem key={idx} slidesVisibleMobile="4">
                                        <ImageTextCarouselItem
                                            {...item}
                                            contentType={contentType}
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                    )}
                    {buttonPosition === 'bottom' && (
                        <div className="cta-container text-center mb-md">
                            {ctaText && (
                                <Button
                                    clickFunction="Link to Page"
                                    style={buttonStyle}
                                    className={cn('text-size-body-lg', buttonClassName)}
                                    url={''}
                                    target={external ? '_blank' : '_self'}
                                    rel={external ? 'noreferrer' : undefined}
                                >
                                    {ctaText}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default ImageTextCarousel
